<template>
  <div v-if="available" class="form form-sm mx-auto">
    <h2 class="mb-4">Two-Factor Authentication</h2>

    <b-list-group>
      <!-- Email: always on, and deliberately no switch. A control that cannot be
           operated is worse than no control - the copy says why instead. -->
      <b-list-group-item class="px-0 border-top-0 border-right-0 border-left-0">
        <strong>Email ({{ emailAddress }})</strong>
        <small class="mt-1 lh-160 d-block"
          >Email is enabled by default and cannot be turned off.<br />
          If text message codes are enabled, email is still available as a
          fallback option.</small
        >
      </b-list-group-item>

      <b-list-group-item
        class="px-0 border-right-0 border-left-0 border-bottom-0 d-flex align-items-center justify-content-between"
      >
        <strong>Text message (SMS)</strong>
        <!-- base-switch declares no `disabled` prop, so :disabled on it would
             land on its root <label> and do nothing. Lock the wrapper and guard
             the handler instead. -->
        <span :class="{ 'sso-2fa-lock': busy }">
          <base-switch
            :key="'sms-' + switchKey"
            :value="smsEnabled"
            @input="onSmsToggle"
          />
        </span>
      </b-list-group-item>
    </b-list-group>

    <template v-if="smsEnabled || editingPhone">
      <!-- Same classes as the Email item's description so the two sections sit
           at the same size and the same distance under their heading. -->
      <small class="mt-1 lh-160 d-block mb-2">{{ phoneHelpText }}</small>

      <template v-if="editingPhone && !verifying">
        <!-- mode="national" so the box reads the way people write their own
             number - (425) 555-1234, 07700 900000 - while the country picker
             carries the dial code. What gets saved is always the E.164 form off
             phoneObject, never this display string. -->
        <div class="sso-tel-wrap">
          <vue-tel-input
            v-model="phoneInput"
            mode="national"
            :default-country="defaultCountry"
            :preferred-countries="['us', 'gb', 'ie', 'ca', 'au']"
            :enabled-country-code="true"
            :enabled-flags="true"
            :valid-characters-only="true"
            :input-options="{ placeholder: 'Mobile number', autocomplete: 'tel' }"
            :class="['sso-tel', { 'sso-tel-invalid': phoneStatus === 'invalid' }]"
            @input="onPhoneInput"
          />
          <!-- Same indicator as the set-password fields: tick once the number
               is valid for the chosen country, cross once it cannot be, and
               nothing at all while the field is empty. -->
          <b-icon-check-circle-fill
            v-if="phoneStatus === 'valid'"
            class="text-success sso-tel-icon"
            title="Valid mobile number for the selected country"
            aria-label="Valid mobile number for the selected country"
          ></b-icon-check-circle-fill>
          <b-icon-x-circle-fill
            v-else-if="phoneStatus === 'invalid'"
            class="text-danger sso-tel-icon"
            title="Not a valid mobile number for the selected country"
            aria-label="Not a valid mobile number for the selected country"
          ></b-icon-x-circle-fill>
        </div>
        <small class="d-block text-muted mb-3 mt-1">
          {{
            phoneStatus === 'invalid'
              ? 'That does not look like a valid mobile number for the selected country.'
              : 'Pick your country, then type the number as you would dial it locally.'
          }}
        </small>
        <b-button
          size="sm"
          variant="primary"
          :disabled="busy || !phoneValid"
          @click="savePhone"
        >
          {{ phoneActionLabel }}
        </b-button>
        <b-button
          size="sm"
          variant="link"
          class="text-muted"
          :disabled="busy"
          @click="cancelPhoneEdit"
          >Cancel</b-button
        >
      </template>

      <!-- Second step. The number is NOT saved yet: until the code below is
           accepted, sign-in codes still go to the previously saved number, so
           abandoning here leaves the user's 2FA exactly as it was. -->
      <template v-else-if="verifying">
        <b-form-input
          v-model="codeInput"
          class="mb-2"
          maxlength="6"
          inputmode="numeric"
          autocomplete="one-time-code"
          placeholder="6-digit code"
          :disabled="busy"
          @keyup.enter="confirmCode"
        ></b-form-input>
        <small class="d-block text-muted mb-3">
          Sent to the number {{ pendingPhoneMasked }}. The code expires in
          {{ pendingExpiryMinutes }} minutes.
        </small>
        <b-button
          size="sm"
          variant="primary"
          :disabled="busy || codeInput.length !== 6"
          @click="confirmCode"
        >
          {{ busy ? 'Checking…' : 'Confirm and save' }}
        </b-button>
        <b-button
          size="sm"
          variant="link"
          class="text-muted"
          :disabled="busy"
          @click="resendCode"
          >Resend</b-button
        >
        <b-button
          size="sm"
          variant="link"
          class="text-muted"
          :disabled="busy"
          @click="cancelVerification"
          >Cancel</b-button
        >
      </template>

      <template v-else>
        <p class="font-weight-bold mb-2 d-flex align-items-center">
          <!-- Reuses vue-tel-input's own sprite rather than a flag emoji, which
               Windows renders as two letters instead of a flag. -->
          <span
            :class="['vti__flag', phoneRegion, 'sso-tel-flag-static']"
            :title="phoneRegionName"
            :aria-label="phoneRegionName"
          ></span>
          <span>{{ phoneDialCode }} {{ phoneDisplay }}</span>
        </p>
        <a
          class="text-sm text-underline"
          href
          @click.prevent="startPhoneEdit"
          >Change mobile number</a
        >
      </template>
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import { defineComponent, ref, computed, onMounted } from '@vue/composition-api'
import { VueTelInput } from 'vue-tel-input'
import PhoneNumber from 'awesome-phonenumber'

import { AuthenticationService } from '@services'

// The number the API stores and returns is E.164 (+14255551234). That is the
// only form Graph accepts, but it is not how anyone reads their own number, so
// it is formatted nationally for display and parsed back on the way in.
//
// This is only the fallback for "no number yet" (new users, or a removed
// number) - once a real number exists, regionOf() below derives the actual
// country from it everywhere (initial load, re-opening the edit field, the
// saved-number display), so this constant never overrides a real number's
// own country.
const DEFAULT_COUNTRY = 'US'

const regionOf = (e164) => {
  if (!e164) return DEFAULT_COUNTRY
  try {
    return new PhoneNumber(e164).getRegionCode() || DEFAULT_COUNTRY
  } catch (e) {
    return DEFAULT_COUNTRY
  }
}

const dialCodeOf = (e164) => {
  if (!e164) return ''
  try {
    const cc = PhoneNumber.getCountryCodeForRegionCode(regionOf(e164))
    return cc ? '+' + cc : ''
  } catch (e) {
    return ''
  }
}

const formatNational = (e164) => {
  if (!e164) return ''
  try {
    const pn = new PhoneNumber(e164)
    // Fall back to what the server gave us rather than showing a blank line if
    // the stored value ever fails to parse.
    return (pn.isValid() && pn.getNumber('national')) || e164
  } catch (e) {
    return e164
  }
}

// Every DRF response is wrapped by api.utils.APIRenderer as {status, data},
// which is why the rest of the app reads response.data.data. Tolerate both
// shapes rather than assuming: reading one level short silently yields
// undefined, which here means the panel hides itself and the legacy one takes
// over - a failure with no error anywhere.
const unwrap = (res) => {
  const body = (res && res.data) || {}
  const wrapped =
    body && typeof body === 'object' && 'status' in body && 'data' in body
  return (wrapped ? body.data : body) || {}
}

const errorDetail = (err) => {
  const body = err && err.response && err.response.data
  if (!body) return null
  const wrapped =
    typeof body === 'object' && 'status' in body && 'data' in body
  const inner = wrapped ? body.data : body
  return (inner && inner.detail) || null
}

export default defineComponent({
  name: 'Form2FASettingsSso',
  components: { VueTelInput },
  setup(props, { emit }) {
    // available stays false until the server confirms this user's 2FA really is
    // managed in Entra. Rendering the panel first and retracting it would show
    // controls that do nothing for a legacy-auth user.
    const available = ref(false)
    const emailAddress = ref('')
    const smsEnabled = ref(false)
    const phoneNumber = ref('')
    const phoneInput = ref('')
    const editingPhone = ref(false)
    const busy = ref(false)
    // Everything vue-tel-input knows about what is currently typed: the parsed
    // forms, the country, and whether it is a real number. Save reads e164 from
    // here; the display string in phoneInput is never sent.
    const phoneObject = ref(null)
    const defaultCountry = ref(DEFAULT_COUNTRY)
    // Second-step state. verifying gates the code box; pendingE164 is kept so
    // Resend does not depend on the number field still being populated, and the
    // masked form is what the server echoed back rather than anything derived
    // here, so the display cannot disagree with what was actually texted.
    const verifying = ref(false)
    const codeInput = ref('')
    const pendingE164 = ref('')
    const pendingPhoneMasked = ref('')
    // Server-driven: false means a changed number is saved outright with no SMS
    // challenge, so the button must say "Save" rather than promise a code.
    const verificationRequired = ref(true)
    const pendingExpiryMinutes = ref(10)
    const phoneValid = computed(
      () => !!(phoneObject.value && phoneObject.value.isValid)
    )
    // Mirrors passwordStatus on the set-password screen: null while the field is
    // empty, so an untouched form is never covered in crosses.
    const phoneStatus = computed(() => {
      if (!phoneInput.value) return null
      return phoneValid.value ? 'valid' : 'invalid'
    })
    const phoneDisplay = computed(() => formatNational(phoneNumber.value))
    // Says what the click will actually do. Promising a code when none is sent
    // is the confusing half; promising a save when a code IS sent is the other.
    const phoneActionLabel = computed(() => {
      if (verificationRequired.value) return busy.value ? 'Sending…' : 'Send code'
      return busy.value ? 'Saving…' : 'Save'
    })
    // Three states now, so a nested ternary in the template stops being readable.
    const phoneHelpText = computed(() => {
      if (verifying.value) return 'Enter the code we sent you to confirm the number.'
      if (editingPhone.value) {
        return verificationRequired.value
          ? 'Enter the mobile number that should receive your codes.'
          : 'Enter the mobile number that should receive your codes. '
            + 'We will email you to confirm the change.'
      }
      return 'Codes are sent to this number.'
    })
    // Lowercase ISO2 is the sprite's own class name (.vti__flag.us).
    const phoneRegion = computed(() => regionOf(phoneNumber.value).toLowerCase())
    const phoneRegionName = computed(() => regionOf(phoneNumber.value))
    const phoneDialCode = computed(() => dialCodeOf(phoneNumber.value))
    // Bumped to remount the switch. The browser flips the checkbox natively, so
    // after a rejected save, writing the prop back to the value it already holds
    // is not a change Vue can see.
    const switchKey = ref(0)

    const applyMethods = (data) => {
      available.value = data.available === true
      verificationRequired.value = data.verification_required !== false
      // Tell the page, so it can show the legacy panel instead of both or neither.
      emit('availability', available.value)
      if (!available.value) return
      emailAddress.value = (data.email && data.email.address) || ''
      smsEnabled.value = !!(data.sms && data.sms.enabled)
      phoneNumber.value = (data.sms && data.sms.phone_number) || ''
      phoneInput.value = phoneNumber.value
      defaultCountry.value = regionOf(phoneNumber.value)
      phoneObject.value = null
      editingPhone.value = false
    }

    const load = async () => {
      const res = await AuthenticationService.ssoMfaMethods().catch(() => null)
      if (!res) {
        // 404 (feature off) or a transport failure. Either way this panel has
        // nothing to say - stay hidden and let the legacy one render.
        emit('availability', false)
        return
      }
      applyMethods(unwrap(res))
    }

    const onSmsToggle = async (next) => {
      if (busy.value) {
        switchKey.value += 1
        return
      }
      if (next) {
        // Turning on cannot be saved without a number, so hold the switch and
        // ask for one. The Save button is what commits it.
        smsEnabled.value = true
        editingPhone.value = true
        phoneInput.value = phoneNumber.value || ''
        return
      }
      await removePhone()
    }

    // vue-tel-input emits (displayString, phoneObject). The object is the useful
    // half - it carries isValid and the E.164 form we actually save.
    const onPhoneInput = (_value, obj) => {
      phoneObject.value = obj || null
    }

    const startPhoneEdit = () => {
      phoneInput.value = phoneNumber.value || ''
      defaultCountry.value = regionOf(phoneNumber.value)
      phoneObject.value = null
      editingPhone.value = true
      // Defensive: a previous attempt left half-finished must not drop the user
      // straight back into a code box for a number they are no longer entering.
      verifying.value = false
      codeInput.value = ''
    }

    const cancelPhoneEdit = () => {
      editingPhone.value = false
      phoneInput.value = phoneNumber.value || ''
      phoneObject.value = null
      // If SMS was never actually on, cancelling must put the switch back.
      if (!phoneNumber.value) {
        smsEnabled.value = false
        switchKey.value += 1
      }
    }

    // Step 1. Sends a code to the typed number and moves to the code box. The
    // number is NOT saved here: nothing reaches Entra until confirmCode
    // succeeds, so a wrong number cannot become the destination for the user's
    // own sign-in codes.
    const savePhone = async () => {
      if (busy.value) return
      // The box shows the national form, so send what the parser produced.
      // Guarded rather than assumed: the button is disabled while invalid, but a
      // stale object would otherwise post the display string.
      const e164 =
        phoneObject.value &&
        phoneObject.value.number &&
        phoneObject.value.number.e164
      if (!e164) {
        Vue.prototype.$toasted.error(
          'Enter a valid mobile number for the selected country.'
        )
        return
      }
      busy.value = true
      try {
        const res = await AuthenticationService.ssoMfaStartPhoneVerification(e164)
        const body = unwrap(res)

        // With verification switched off the server saves the number outright
        // and answers with the same {sms:...} shape the confirm step returns.
        // Detect that rather than assume a code was sent, or the user is left
        // staring at a code box nothing will ever fill.
        if (body.sms) {
          phoneNumber.value = body.sms.phone_number || e164
          defaultCountry.value = regionOf(phoneNumber.value)
          smsEnabled.value = true
          verifying.value = false
          editingPhone.value = false
          codeInput.value = ''
          Vue.prototype.$toasted.show(
            'Your mobile number has been updated. We have emailed you to confirm the change.'
          )
          return
        }

        const v = body.verification || {}
        pendingE164.value = e164
        pendingPhoneMasked.value = v.phone_number || ''
        pendingExpiryMinutes.value = v.expires_in_minutes || 10
        codeInput.value = ''
        verifying.value = true
        Vue.prototype.$toasted.show('We have sent you a code by text message.')
      } catch (err) {
        Vue.prototype.$toasted.error(
          errorDetail(err) || 'That code could not be sent. Please try again.'
        )
      } finally {
        busy.value = false
      }
    }

    // Step 2. The only call that saves the number, so success here is the first
    // moment the user's sign-in codes change destination.
    const confirmCode = async () => {
      if (busy.value || codeInput.value.length !== 6) return
      busy.value = true
      try {
        const res = await AuthenticationService.ssoMfaConfirmPhoneVerification(
          codeInput.value
        )
        const sms = unwrap(res).sms || {}
        phoneNumber.value = sms.phone_number || pendingE164.value
        defaultCountry.value = regionOf(phoneNumber.value)
        smsEnabled.value = true
        verifying.value = false
        editingPhone.value = false
        codeInput.value = ''
        Vue.prototype.$toasted.show('Text message codes are now enabled.')
      } catch (err) {
        // The server counts the attempt and says how many are left, so its
        // message is the useful one - don't replace it with a generic string.
        Vue.prototype.$toasted.error(
          errorDetail(err) || 'That code could not be confirmed.'
        )
      } finally {
        busy.value = false
      }
    }

    // Re-issues a code to the same number. Goes back through step 1 rather than
    // having its own endpoint, because the server replaces any pending code -
    // so a resend and a corrected number are the same operation.
    const resendCode = async () => {
      if (busy.value || !pendingE164.value) return
      busy.value = true
      try {
        await AuthenticationService.ssoMfaStartPhoneVerification(pendingE164.value)
        codeInput.value = ''
        Vue.prototype.$toasted.show('We have sent you another code.')
      } catch (err) {
        Vue.prototype.$toasted.error(
          errorDetail(err) || 'That code could not be sent. Please try again.'
        )
      } finally {
        busy.value = false
      }
    }

    // Tells the server to drop the candidate, so an abandoned change does not
    // sit around able to be confirmed later.
    const cancelVerification = async () => {
      verifying.value = false
      codeInput.value = ''
      pendingE164.value = ''
      pendingPhoneMasked.value = ''
      await AuthenticationService.ssoMfaCancelPhoneVerification().catch(() => null)
      cancelPhoneEdit()
    }

    const removePhone = async () => {
      busy.value = true
      try {
        await AuthenticationService.ssoMfaRemovePhone()
        smsEnabled.value = false
        phoneNumber.value = ''
        phoneInput.value = ''
        phoneObject.value = null
        defaultCountry.value = DEFAULT_COUNTRY
        editingPhone.value = false
        // The server discards the candidate too; clearing here keeps the UI
        // from offering to confirm a number that can no longer be saved.
        verifying.value = false
        codeInput.value = ''
        Vue.prototype.$toasted.show(
          'Text message codes are off. You will receive codes by email.'
        )
      } catch (err) {
        Vue.prototype.$toasted.error(
          errorDetail(err) || 'That change could not be saved. Please try again.'
        )
        // The server still holds SMS on, so put the switch back.
        smsEnabled.value = true
      } finally {
        busy.value = false
        switchKey.value += 1
      }
    }

    onMounted(load)

    return {
      available,
      emailAddress,
      smsEnabled,
      phoneNumber,
      phoneDisplay,
      phoneActionLabel,
      phoneRegion,
      phoneRegionName,
      phoneDialCode,
      phoneInput,
      phoneValid,
      phoneStatus,
      phoneHelpText,
      defaultCountry,
      editingPhone,
      busy,
      switchKey,
      verifying,
      codeInput,
      pendingPhoneMasked,
      pendingExpiryMinutes,
      onSmsToggle,
      onPhoneInput,
      startPhoneEdit,
      cancelPhoneEdit,
      savePhone,
      confirmCode,
      resendCode,
      cancelVerification
    }
  }
})
</script>

<style scoped>
/* Stops the mouse reaching the checkbox while a save is in flight. Not enough on
   its own - it does not remove the input from the tab order - so onSmsToggle
   guards as well. */
.sso-2fa-lock {
  pointer-events: none;
  opacity: 0.5;
}

/* vue-tel-input ships its own look, which sits oddly next to the BootstrapVue
   inputs on the rest of this page. These pull it back to .form-control: same
   height, radius, border and focus ring. ::v-deep because the library's markup
   is not scoped to this component. */
.sso-tel ::v-deep .vti__input,
.sso-tel {
  font-size: 0.875rem;
  line-height: 1.5;
}

.sso-tel {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  height: calc(1.5em + 1.25rem + 2px);
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.sso-tel:focus-within {
  border-color: #8bb4f7;
  box-shadow: 0 0 0 0.2rem rgba(50, 151, 211, 0.25);
}

.sso-tel.sso-tel-invalid {
  border-color: #fd5d93;
}

.sso-tel.sso-tel-invalid:focus-within {
  box-shadow: 0 0 0 0.2rem rgba(253, 93, 147, 0.25);
}

.sso-tel-wrap {
  position: relative;
}

/* Floated over the field rather than sitting beside it, matching how
   BasePasswordInput handles its tick. pointer-events off so the icon never
   steals a click meant for the input. */
.sso-tel-icon {
  position: absolute;
  top: 50%;
  right: 0.75rem;
  transform: translateY(-50%);
  z-index: 4;
  pointer-events: none;
}

.sso-tel ::v-deep .vti__input {
  border-radius: 0 0.375rem 0.375rem 0;
  /* Room for the indicator, reserved whether or not it is showing, so the
     number does not shift sideways the moment it becomes valid. */
  padding-right: 2.5rem;
}

.sso-tel ::v-deep .vti__dropdown {
  border-radius: 0.375rem 0 0 0.375rem;
  padding: 0 0.5rem;
}

/* The flags are one 20x15 bitmap sprite positioned per country, so width and
   height cannot be changed - that would slide the neighbouring flags into view.
   Scaling the whole box keeps each country's window intact. transform does not
   affect layout, hence the margin to stop the enlarged flag colliding with the
   dial code beside it. */
.sso-tel ::v-deep .vti__flag {
  transform: scale(1.35);
  transform-origin: center;
  margin-right: 0.45rem;
  margin-left: 0.15rem;
}

.sso-tel ::v-deep .vti__dropdown-item .vti__flag {
  margin-right: 0.6rem;
}

.sso-tel ::v-deep .vti__dropdown:hover,
.sso-tel ::v-deep .vti__dropdown.open {
  background-color: #f6f9fc;
}

/* 240-odd countries with no search box in v4, so give the list room and keep
   the preferred ones reachable without a long scroll. */
.sso-tel ::v-deep .vti__dropdown-list {
  z-index: 5;
  max-height: 240px;
  border-radius: 0.375rem;
  border-color: #dee2e6;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.sso-tel ::v-deep .vti__dropdown-item {
  padding: 0.35rem 0.75rem;
}

.sso-tel ::v-deep .vti__dropdown-item.highlighted {
  background-color: #f6f9fc;
}

/* The saved-number line sits outside .sso-tel, so it needs its own copy of the
   scale - same 1.35 as the field, so the two never look like different flags. */
.sso-tel-flag-static {
  display: inline-block;
  flex: 0 0 auto;
  transform: scale(1.35);
  transform-origin: center;
  margin-right: 0.7rem;
  margin-left: 0.15rem;
}
</style>
